import React, { useState } from "react";
import { Clock, GitBranch, Mail, MessageSquare, Phone, Plus, Smartphone, Users, Zap, ChevronLeft, ChevronRight } from "lucide-react";
import { NodePaletteProps, NodeType } from "../../../../pages/journey/types";


const nodeTypes: NodeType[] = [
  { type: "trigger", label: "Trigger", icon: Zap, color: "bg-emerald-500", bgColor: "bg-emerald-50", textColor: "text-emerald-700" },
  { type: "segment", label: "Segment", icon: Users, color: "bg-blue-500", bgColor: "bg-blue-50", textColor: "text-blue-700" },
  { type: "decision", label: "Decision", icon: GitBranch, color: "bg-orange-500", bgColor: "bg-orange-50", textColor: "text-orange-700" },
  { type: "delay", label: "Delay", icon: Clock, color: "bg-pink-500", bgColor: "bg-pink-50", textColor: "text-pink-700" },
  { type: "sms", label: "SMS", icon: MessageSquare, color: "bg-emerald-500", bgColor: "bg-emerald-50", textColor: "text-emerald-700" },
  { type: "whatsapp", label: "WhatsApp", icon: MessageSquare, color: "bg-emerald-500", bgColor: "bg-emerald-50", textColor: "text-emerald-700" },
  { type: "ai_call", label: "AI Call", icon: Phone, color: "bg-purple-500", bgColor: "bg-purple-50", textColor: "text-purple-700" },
  { type: "rcs", label: "RCS", icon: Smartphone, color: "bg-blue-500", bgColor: "bg-blue-50", textColor: "text-blue-700" },
  { type: "email", label: "Email", icon: Mail, color: "bg-blue-500", bgColor: "bg-blue-50", textColor: "text-blue-700" },
];

interface ExtendedNodePaletteProps extends NodePaletteProps {
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onNodeTypeSelect?: (type: string) => void;
}

export const NodePalette: React.FC<ExtendedNodePaletteProps> = ({
  onDragStart,
  onAddNode,
  onNodeTypeSelect,
  isCollapsed = false,
  onToggleCollapse
}) => {
   const [search, setSearch] = useState("");

  const filteredNodes = nodeTypes.filter((node) =>
    node.label.toLowerCase().includes(search.toLowerCase())
  );
  const handleNodeClick = (type: string) => {
    if (onNodeTypeSelect) {
      onNodeTypeSelect(type);
    } else {
      onAddNode(type);
    }
  };

  if (isCollapsed) {
    return (
      <div className="w-12 bg-gray-50 border-r border-gray-200 p-2 flex flex-col items-center">
        <button
          onClick={onToggleCollapse}
          className="p-2 hover:bg-gray-100 rounded mb-4"
          title="Expand node palette"
        >
          <ChevronRight className="w-4 h-4 text-gray-600" />
        </button>

        <div className="space-y-2">

          {filteredNodes.slice(0, 5).map(({ type, icon: Icon, color }) => (
            <button
              key={type}
              onClick={() => handleNodeClick(type)}
              className="p-2 hover:bg-gray-100 rounded transition-colors"
              title={type.charAt(0).toUpperCase() + type.slice(1)}
            >
              <div className={`p-1 rounded ${color}`}>
                <Icon className="w-3 h-3 text-white" />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-64 bg-gray-50 border-r border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700">NODE PALETTE</h3>
        <button
          onClick={onToggleCollapse}
          className="p-1 hover:bg-gray-100 rounded"
          title="Collapse node palette"
        >
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>
      </div>
      <input
        type="text"
        placeholder="Search nodes..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4 text-sm"
      />
      <div className="space-y-2">
        {filteredNodes.map(({ type, label, icon: Icon, color, bgColor, textColor }) => (
          <div
            key={type}
            draggable
            onDragStart={(e) => onDragStart(e, type)}
            className="group flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 cursor-move hover:shadow-md transition-all"
          >
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${color}`}>
                <Icon className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700">{label}</span>
            </div>
            <button
              onClick={() => handleNodeClick(type)}
              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-100 rounded transition-opacity"
              title={`Add ${label}`}
            >
              <Plus className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-8 text-xs text-gray-500">
        <p>Drag items onto the canvas or click the + button to add.</p>
      </div>
    </div>
  );
};